<template>
  <a-row :gutter="[16,16]">
    <a-col
      :md="8"
      :span="24">
      <image-carousel v-if="plan.pictures?.length">
        <template #images>
          <div
            v-for="(item, index) in plan?.pictures"
            :key="`${item}-${index}`">
            <a-card>
              <image-viewer
                :images="[item]"
                :enable-upload="canUploadImage && enableImageUpload && Boolean(servicePlanId)"
                :on-upload="(file) => beforeUpload(file, index)">
                <img
                  :style="{ width: '100%', objectFit: 'contain', marginBottom: '2rem', height: '300px' }"
                  :src="item"
                  alt="">
              </image-viewer>
              <a-card-meta :title="`${index + 1}/${plan.pictures.length} этаж`" />
            </a-card>
          </div>
        </template>
      </image-carousel>
    </a-col>

    <a-col
      :md="16"
      :span="24">
      <a-row :gutter="[16,16]">
        <a-col
          v-if="isSamePlansAiVisible"
          :span="24">
          <a-button
            :disabled="!plan?.pictures || plan.pictures.length > 1"
            :style="{ width: '100%' }"
            @click="openSamePlansAiModal">
            Поиск похожих планировок
            <a-tag
              color="#f50"
              :style="{marginLeft: '.5rem'}">
              Beta
            </a-tag>
            <a-tooltip
              v-if="plan?.pictures?.length > 1"
              placement="right">
              <template #title>
                <span>Работает только с одноэтажными планировками</span>
              </template>
              <question-circle-outlined style="margin-left: 5px" />
            </a-tooltip>
          </a-button>
        </a-col>
        <a-col
          v-if="isSearchButtonVisible"
          :span="24">
          <router-link
            :disabled="!servicePlanId"
            :to="{ name: 'crm-search-reference', params: { crm_id: plan.id } }">
            <a-button :style="{ width: '100%' }">
              Похожие планировки в Поиске 2.0
              <a-tag
                color="#45A5FF"
                :style="{marginLeft: '.5rem'}">
                New
              </a-tag>
            </a-button>
          </router-link>
        </a-col>
        <a-col
          :span="24"
          :md="12">
          <a-button
            :disabled="!plan.pictures"
            :style="{ width: '100%' }"
            @click="openCompareModal">
            Открыть сравнение
          </a-button>
        </a-col>
        <a-col
          v-if="planType === PlanType.Type2d"
          :span="24"
          :md="12">
          <a-button
            :style="{ width: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }"
            @click="downloadGluedImage">
            Скачать результат (многоэтажная обработка)
          </a-button>
        </a-col>
        <a-col
          v-if="planType === PlanType.Type2d"
          :span="24"
          :md="12">
          <a-button
            :style="{ width: '100%' }"
            :href="plan?.links?.neural?.editor"
            target="_blank"
            rel="noopener noreferrer">
            Открыть Конструктор
          </a-button>
        </a-col>

        <a-col
          v-if="canViewServicePlan && servicePlanId"
          :span="24"
          :md="12">
          <router-link
            :disabled="!servicePlanId"
            :to="{ name: 'plans-show', params: { id: servicePlanId } }">
            <a-button :style="{ width: '100%' }">
              [Сервис] Открыть планировку
            </a-button>
          </router-link>
        </a-col>
        <a-col
          v-if="canSearchPlans"
          :span="24"
          :md="12">
          <a-button
            :style="{ width: '100%' }"
            @click="openModal">
            Поиск похожих планировок
          </a-button>
        </a-col>
        <a-col
          :span="24"
          :md="12">
          <merge-crm-plan-button
            :current-plan-id="plan.id"
            :type="planType"
            text-button="Слияние планировки"
            @after-ok="loadData" />
        </a-col>
        <a-col
          v-if="isWidgetMaterialVisible && planType === PlanType.Type3d"
          :span="24">
          <a-button
            :style="{ width: '100%' }"
            @click="openMaterialSelectionModal">
            Выбор вариантов
          </a-button>
        </a-col>
        <a-col
          v-if="publicPlanUrl"
          :span="24">
          <a-button
            :style="{ width: '100%' }"
            :href="publicPlanUrl"
            target="_blank"
            rel="noopener noreferrer">
            Открыть публичную ссылку
          </a-button>
        </a-col>

        <a-col
          v-if="!plan?.bitrix_leads?.length && plan?.apartment?.integration_with_bitrix"
          :span="24"
          :md="24">
          <add-bitrix-lead-button
            :current-plan-id="plan.id"
            text-button="Добавить ссылку на Bitrix"
            @after-ok="loadData" />
        </a-col>

        <a-col
          :span="24"
          :md="24">
          <a-button
            :style="{ width: '100%' }"
            @click="openVerificationModal">
            Запустить автопроверку
            <a-tag
              color="#45A5FF"
              :style="{marginLeft: '.5rem'}">
              New
            </a-tag>
          </a-button>
        </a-col>

        <a-col
          v-if="canLinkServicePlan"
          :span="24"
          :md="24">
          <a-button
            :style="{ width: '100%' }"
            @click="isOverrideModalVisible = true">
            Привязать новую сервисную планировку
            <a-tag
              color="#45A5FF"
              :style="{marginLeft: '.5rem'}">
              New
            </a-tag>
          </a-button>
        </a-col>
      </a-row>

      <div>
        <slot name="after-controls" />
      </div>

      <div v-if="plan?.bitrix_leads?.length">
        <a-divider>Лиды</a-divider>
        <card :style="{ display: 'flex', gap: '20px', flexWrap: 'wrap' }">
          <div
            v-for="lead in plan.bitrix_leads"
            :key="lead.id"
            style="min-width: 200px;">
            <a-space size="middle">
              <a
                :href="bitrixLeadUrl(lead.lead_id)"
                target="_blank"
                rel="noopener noreferrer">
                Заявка №{{ lead.lead_id }}
              </a>
              <a-popconfirm
                title="Вы точно хотите удалить этот лид?"
                ok-text="Да"
                cancel-text="Нет"
                @confirm="removeBitrixLead(plan.id, lead.lead_id)">
                <a-button
                  type="primary"
                  danger
                  size="small"
                  style="margin-left: 16px">
                  Удалить
                </a-button>
              </a-popconfirm>
            </a-space>
          </div>
        </card>
      </div>

      <a-modal
        v-model:open="isVerificationModalVisible"
        title="Подтверждение параметров проверки"
        :confirm-loading="confirmLoading"
        ok-text="Подтвердить"
        cancel-text="Отмена"
        centered
        @ok="handleVerificationModalOk"
        @cancel="handleVerificationModalCancel">
        <a-form layout="vertical">
          <a-form-item label="Чеклисты">
            <a-select
              v-model:value="selectedChecklists"
              mode="multiple"
              :options="checklistOptions"
              placeholder="Выберите чеклисты..."
              style="width: 100%" />
          </a-form-item>

          <a-form-item
            label="Своя инструкция">
            <a-textarea
              v-model:value="customChecklist"
              placeholder="Введите ваши дополнительные инструкции..."
              :rows="4"
              show-count />
          </a-form-item>
        </a-form>
      </a-modal>

      <plan-description
        title="Описание"
        :description="plan?.description"
        :value="plan.description"
        :is-editing-available="true"
        @change-description="changeDescription" />

      <plan-description
        title="Описание апартамента"
        :description="plan?.apartment?.description" />

      <div v-if="plan?.references?.length > 0 && isPlanDescriptionAndRendersVisible">
        <a-divider>Референсы</a-divider>
        <image-viewer
          :style="{display: 'flex', gap: '20px', flexWrap: 'wrap'}"
          :images="plan.references">
          <div
            v-for="(item, index) in plan.references"
            :key="`${item}-${index}`">
            <img
              :style="{'object-fit': 'contain'}"
              :src="item"
              loading="lazy"
              height="100"
              width="100"
              alt="References">
          </div>
        </image-viewer>
      </div>
    </a-col>
  </a-row>

  <a-modal
    v-if="isSamePlansAiModalVisible"
    v-model:open="isSamePlansAiModalVisible"
    :footer="null"
    width="100%"
    wrap-class-name="full-modal"
    @close="closeSamePlansAiModal">
    <template #title>
      Поиск похожих планировок
      <a-tag
        color="#f50">
        Beta
      </a-tag>
    </template>
    <same-plans-ai-list
      :plan-type="type"
      :plan-data="planData"
      :original-plan-img="plan?.picture"
      :plan-image="planData?.pictures"
      @close="closeSamePlansAiModal" />
  </a-modal>

  <a-modal
    :open="isCompareModalVisible"
    :destroy-on-close="true"
    :footer="null"
    title="Сравнение"
    centered
    :style="{ width: '100%', top: '8px' }"
    @cancel="closeCompareModal">
    <plan-compare-carousel
      :originals="compareData.originals"
      :svgs="compareData.svgs"
      :top-views="filteredTopViews" />
  </a-modal>

  <a-modal
    v-if="samePlansVisible"
    v-model:open="samePlansVisible"
    :footer="null"
    title="Поиск похожих планировок"
    width="100%"
    wrap-class-name="full-modal"
    @close="closeModal">
    <same-plans-list
      :plan-data="plan"
      :type="planType"
      :plan-image="plan.pictures"
      @close="closeModal"
      @update-page="loadData" />
  </a-modal>

  <material-selection-modal
    :open="isMaterialSelectionModalVisible"
    :service-plan-id="plan?.plan?.id ?? null"
    :crm-plan-id="plan?.id ?? null"
    :crm-plan-widget-variants="plan?.widget_variants ?? []"
    visible
    @close="closeMaterialSelectionModal" />

  <override-plan-modal
    :visible="isOverrideModalVisible"
    :crm-plan-id="plan.id"
    @close="isOverrideModalVisible = false"
    @success="handleOverrideSuccess" />
</template>

<script setup>
import {
  ref, computed,
} from 'vue';
import { useStore } from 'vuex';
import { httpClient } from '@/httpClient';
import { api } from '@/api';
import {
  QuestionCircleOutlined,
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import ImageCarousel from '@/Components/Common/ImageCarousel.vue';
import MergeCrmPlanButton from '@/Components/CRM/Plans/MergeCrmPlanButton.vue';
import AddBitrixLeadButton from '@/Components/CRM/Plans/AddBitrixLeadButton.vue';
import SamePlansAiList from '@/Components/CRM/Plans/SamePlansAiList.vue';
import PlanCompareCarousel from '@/Components/CRM/Plans/CompareCarousel.vue';
import SamePlansList from '@/Components/CRM/Plans/SamePlansList.vue';
import { trackGoal } from '@/helpers/tracking';
import getFlag from '@/helpers/getFlag';
import ImageViewer from '@/Components/Common/ImageViewer.vue';
import MaterialSelectionModal from '@/Components/CRM/Plans/Modals/MaterialSelectionModal.vue';
import { PlanType } from '@/enums/planType';
import { downloadFile } from '@/helpers/downloadFile';
import config from '@/config'; // eslint-disable-line import/extensions
import OverridePlanModal from '@/Components/CRM/Plans/Modals/OverridePlanModal.vue';
import PlanDescription from './PlanDescription.vue';

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
  planId: {
    type: Number,
    default: 1,
  },
  apartmentDescription: {
    type: String,
    default: '',
  },
  loadData: {
    type: Function,
    required: true,
  },
  planType: {
    type: String,
    default: '',
  },
  servicePlanId: {
    type: String,
    default: '',
  },
  compareData: {
    type: Object,
    required: true,
  },
  enableImageUpload: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const canUploadImage = computed(() => store.getters['auth/hasPermission']('api.crm.plans.upload_image'));
const canViewServicePlan = computed(() => store.getters['auth/hasPermission']('ui.page.plans-show'));
const canSearchPlans = computed(() => store.getters['auth/hasPermission']('ui.page.crm-search'));
const canLinkServicePlan = computed(() => store.getters['auth/hasPermission']('api.crm.plans.link_service_plan'));

const isSamePlansAiVisible = getFlag('isSamePlansAiVisible');
const isSearchButtonVisible = getFlag('isSearchButtonVisible');
const isWidgetMaterialVisible = getFlag('isWidgetMaterialVisible');
const isPlanDescriptionAndRendersVisible = getFlag('isPlanDescriptionAndRendersVisible');

const samePlansVisible = ref(false);
const isSamePlansAiModalVisible = ref(false);
const isCompareModalVisible = ref(false);
const isMaterialSelectionModalVisible = ref(false);

const isVerificationModalVisible = ref(false);
const confirmLoading = ref(false);
const checklists = ref([]);
const selectedChecklists = ref([]);
const customChecklist = ref('');

const isOverrideModalVisible = ref(false);

const emit = defineEmits(['verification-started']);

const checklistOptions = computed(() => checklists.value.map((item) => ({
  label: item.name,
  value: item.id,
})));

const filteredTopViews = computed(() => {
  if (!props.compareData?.topViews) return [];

  return props.compareData.topViews.filter((view) => !view?.includes('Masked'));
});

const loadChecklists = async () => {
  try {
    const response = await api.getCrmChecklists(props.plan.id);
    const { data } = response;
    checklists.value = [
      ...data.default_checklists,
      ...data.available_checklists,
    ];
    selectedChecklists.value = data.default_checklists.map((item) => item.id);
  } catch (error) {
    message.error('Не удалось загрузить список чеклистов');
  }
};

const openVerificationModal = () => {
  isVerificationModalVisible.value = true;
  loadChecklists();
};

const handleVerificationModalOk = async () => {
  confirmLoading.value = true;
  try {
    const requestData = {
      planId: props.plan.id,
      type: props.planType,
      checklists: selectedChecklists.value,
      custom_checklist: customChecklist.value.trim(),
    };

    await api.reviewCrmPlan(requestData);
    message.success('Проверка запущена');
    emit('verification-started', {
      checklistsCount: selectedChecklists.value.length,
      hasCustomChecklist: !!customChecklist.value.trim(),
    });
  } catch (error) {
    if (error?.response?.status === 403) {
      const messageText = error?.response?.data?.message || 'Доступ запрещен';
      message.error(messageText);
    } else {
      message.error('Произошла ошибка при отправке данных');
    }
  }

  isVerificationModalVisible.value = false;
  selectedChecklists.value = [];
  customChecklist.value = '';
  confirmLoading.value = false;
};

const handleVerificationModalCancel = () => {
  isVerificationModalVisible.value = false;
  selectedChecklists.value = [];
  customChecklist.value = '';
  confirmLoading.value = false;
};

const openModal = () => {
  trackGoal('same_plans_search');
  samePlansVisible.value = true;
};

const closeModal = () => {
  samePlansVisible.value = false;
};

const openSamePlansAiModal = () => {
  trackGoal('same_plans_ai_search');
  isSamePlansAiModalVisible.value = true;
};

const closeSamePlansAiModal = () => {
  isSamePlansAiModalVisible.value = false;
};

const openCompareModal = () => (isCompareModalVisible.value = true);

const closeCompareModal = () => {
  isCompareModalVisible.value = false;
};

const openMaterialSelectionModal = () => {
  isMaterialSelectionModalVisible.value = true;
};

const closeMaterialSelectionModal = () => {
  isMaterialSelectionModalVisible.value = false;
};

const downloadGluedImage = () => {
  const messageKey = `download-glued-image-${Date.now()}`;
  message.loading({
    duration: 0,
    content: 'Генерируем изображение',
    key: messageKey,
  });

  httpClient.get(`/api/crm/plans/${props.plan.id}/get_glued_picture`)
    .then(({ data }) => {
      if (data?.url) {
        const fileName = `glued_image_${props.plan.id}.jpg`;
        downloadFile(data.url, fileName);
      }
    })
    .catch(() => {
      message.error('Что-то пошло не так :(');
    })
    .finally(() => {
      message.destroy(messageKey);
    });
};

const publicPlanUrl = computed(() => {
  const { servicePlanId } = props;
  const { publicUrl } = config;
  if (!servicePlanId || !publicUrl) {
    return null;
  }

  return `${publicUrl}/${servicePlanId}`;
});

const changeDescription = (newDescription) => {
  store.dispatch('crm/changePlanDescription', { id: props.planId, description: newDescription })
    .finally(() => {
      props.loadData();
    });
};

const bitrixLeadUrl = (leadId) => `${config.bitrixUrl}/crm/lead/details/${leadId}/`;

const removeBitrixLead = (crmPlanId, leadId) => {
  api.removeBitrixLeadToCrmPlan(crmPlanId, leadId)
    .then(({ data }) => {
      message.success(`Лид №${leadId} удалён`);
      props.loadData();
    })
    .catch((error) => {
      message.error(error.message || 'Что-то пошло не так :(');
    });
};

const beforeUpload = async (file, floorIndex) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('floor', floorIndex);

  const messageKey = `upload-image-${Date.now()}`;
  message.loading({
    duration: 0,
    content: 'Загружаем изображение',
    key: messageKey,
  });

  await httpClient.post(`/api/plans/${props.servicePlanId}/floor_images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .then(() => {
      message.success('Изображение успешно обновлено');
      props.loadData();
    })
    .catch((error) => {
      console.error('Upload error:', error);
      message.error('Ошибка при загрузке изображения');
    })
    .finally(() => {
      message.destroy(messageKey);
    });

  return false;
};

const handleOverrideSuccess = () => {
  message.success('Plan updated successfully');
  props.loadData();
};
</script>
